import { useEffect, useState } from 'react';
import {
  LIMITS,
  formatBytes,
  type AttachmentDto,
  type AttachmentKind,
  type MessageType,
} from '@initiative/shared';
import { API_BASE } from '../../lib/api.js';
import { maxUploadBytesFuer } from '../../lib/uploadGrenzen.js';
import type { OutboxAttachment } from '../../lib/db.js';
import { useChat } from '../../state/chat.js';
import { toast } from '../../state/ui.js';

/**
 * Small helpers shared by the media sheets and the chat bubbles. Everything
 * that touches the browser media APIs is funnelled through here so the
 * components stay readable and the quirks (codec suffixes, iOS fallbacks,
 * upload limits) live in exactly one place.
 */

/** `video/webm;codecs=vp9` → `video/webm` – the API only allows bare mime types. */
export function baseMime(mime: string): string {
  return (mime || '').split(';')[0].trim().toLowerCase();
}

/** Attachment URLs are relative to the API, which may live on another host. */
export function mediaSrc(attachment: AttachmentDto): string {
  const url = attachment.url;
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//')) return url;
  return `${API_BASE}${url}`;
}

/**
 * Ein Miniaturbild vom Server – für Kacheln, nicht für Blasen.
 *
 * # Warum es das braucht
 *
 * In einer Kachelansicht war die eingebettete `previewDataUrl` bisher das
 * ENDGÜLTIGE Bild: Anders als in der Chatblase wird daneben nichts Schärferes
 * nachgeladen. Und sie darf nicht wachsen – sie fährt in jeder
 * Nachrichtenliste mit, als Text in einer Spalte.
 *
 * Das Original zu laden wäre die falsche Antwort: ein Ordner mit vierzig
 * Urlaubsfotos zu je zwei Megabyte, für Kacheln von 110 Punkten.
 *
 * # Die Kantenlänge
 *
 * `kante` ist die CSS-Grösse der Kachel; hier wird mit der Punktdichte
 * multipliziert. Auf einem Telefon mit dreifacher Dichte hat eine Kachel von
 * 110 Punkten in Wahrheit 330 – ein Bild von 110 wäre dort genauso unscharf
 * wie vorher. Der Server rundet auf die nächste angebotene Stufe auf.
 */
export function miniaturSrc(attachment: AttachmentDto, kante: number): string {
  /*
   * Die Punktdichte zählt bis zwei, nicht bis drei.
   *
   * Der Server hat drei Stufen (160, 320, 640) und rundet auf. Eine Kachel
   * von 160 Punkten auf einem Telefon mit dreifacher Dichte verlangte damit
   * 480 und bekäme 640 – nachgemessen 451 kB statt 75 kB für DIESELBE
   * Kachel. Bei zwei ist es 320, also auf einem 2×-Schirm genau eins zu eins
   * und auf einem 3×-Schirm ein wenig weich. Für ein Miniaturbild, das man im
   * Vorbeigehen ansieht, ist das der richtige Tausch; wer genau hinsehen
   * will, tippt darauf und bekommt das Original.
   */
  const dichte = typeof window !== 'undefined' ? Math.min(2, window.devicePixelRatio || 1) : 1;
  return `${mediaSrc(attachment)}/miniatur?kante=${Math.round(kante * dichte)}`;
}

/**
 * Die Bilddaten eines Anhangs – zum Lesen, nicht zum Anzeigen.
 *
 * `mediaSrc` reicht für `<img src>`: Der Browser folgt der Umleitung zum
 * Speicher und zeigt an. Wer die Punkte *lesen* will (bearbeiten, freistellen),
 * braucht den geraden Weg über die API: Nach einer Umleitung auf eine andere
 * Herkunft schickt der Browser `Origin: null`, die CORS-Regel des Speichers
 * greift nicht mehr, und die Leinwand wäre anschliessend „verunreinigt“ – man
 * sähe das Bild, könnte es aber nicht speichern.
 *
 * `credentials: 'include'`, weil die Medienrouten seit dem Medien-Keks eine
 * angemeldete Person verlangen. Bei einem `<img>` schickt der Browser den Keks
 * von selbst; ein `fetch` tut das zu einer anderen Herkunft nur auf
 * ausdrückliche Bitte. Hier stand deshalb früher das Gegenteil, mit der
 * Begründung, diese Routen kennten keinen Benutzer.
 *
 * Voraussetzung dafür ist, dass `CORS_ORIGINS` ausdrücklich gesetzt ist: Bei
 * `*` schaltet der Server `allow_credentials(false)`, und der Browser verwirft
 * die Antwort. Dasselbe gilt für den Keks selbst.
 */
export async function mediaBytes(attachment: AttachmentDto): Promise<Blob> {
  const antwort = await fetch(`${mediaSrc(attachment)}/bytes`, { credentials: 'include' });
  if (!antwort.ok) throw new Error(`Das Bild konnte nicht geladen werden (${antwort.status})`);
  return await antwort.blob();
}

/**
 * Dieselbe Datei, aber zum Speichern statt zum Anzeigen.
 *
 * Ein `download`-Attribut an einem Verweis wird von jedem Browser ignoriert,
 * wenn das Ziel auf einer anderen Herkunft liegt – und die API liegt das. Der
 * Verweis oeffnete die Datei also, statt sie zu sichern, und in der
 * installierten App auf dem iPhone geschah gar nichts.
 *
 * Die API kann es besser: `/download` schickt `Content-Disposition:
 * attachment`, und daran haelt sich jeder Browser. Kein Umweg ueber den
 * Speicher der App, kein Blob im Arbeitsspeicher – auch ein Video von 300 MB
 * geht so.
 */
export function mediaDownloadSrc(attachment: AttachmentDto): string {
  return `${mediaSrc(attachment)}/download`;
}

const EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/quicktime': 'mov',
  'video/x-matroska': 'mkv',
  'audio/webm': 'webm',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/aac': 'aac',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
};

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heic',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mkv: 'video/x-matroska',
  mp3: 'audio/mpeg',
  m4a: 'audio/mp4',
  aac: 'audio/aac',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  opus: 'audio/ogg',
  pdf: 'application/pdf',
};

function extensionOf(fileName: string | null | undefined): string {
  if (!fileName || !fileName.includes('.')) return '';
  const parts = fileName.split('.');
  return (parts[parts.length - 1] || '').toLowerCase();
}

export function extensionFor(mime: string): string {
  const base = baseMime(mime);
  const known = EXTENSIONS[base];
  if (known) return known;
  const parts = base.split('/');
  const guess = (parts[1] || '').replace(/[^a-z0-9]/g, '');
  return guess.length > 0 ? guess : 'bin';
}

/** `foto-20260824-101500.webp` – readable in every downloads folder. */
export function timestampName(prefix: string, mime: string): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `${prefix}-${stamp}.${extensionFor(mime)}`;
}

export function kindForFile(file: File): AttachmentKind {
  const mime = baseMime(file.type);
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  const mapped = MIME_BY_EXTENSION[extensionOf(file.name)];
  if (mapped?.startsWith('image/')) return 'image';
  if (mapped?.startsWith('video/')) return 'video';
  if (mapped?.startsWith('audio/')) return 'audio';
  return 'file';
}

/** Android sometimes hands over files without a mime type. */
export function mimeForFile(file: File): string {
  if (file.type) return baseMime(file.type);
  return MIME_BY_EXTENSION[extensionOf(file.name)] ?? 'application/octet-stream';
}

export function fileIconFor(mime: string, fileName?: string | null): string {
  const base = baseMime(mime);
  const ext = extensionOf(fileName);
  if (base.startsWith('image/')) return '🖼️';
  if (base.startsWith('video/')) return '🎬';
  if (base.startsWith('audio/')) return '🎧';
  if (base === 'application/pdf' || ext === 'pdf') return '📕';
  if (
    base.includes('zip') ||
    base.includes('compressed') ||
    ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)
  )
    return '🗜️';
  if (base.includes('word') || ['doc', 'docx', 'odt', 'rtf'].includes(ext)) return '📘';
  if (
    base.includes('sheet') ||
    base.includes('excel') ||
    ['xls', 'xlsx', 'ods', 'csv'].includes(ext)
  )
    return '📗';
  if (
    base.includes('presentation') ||
    base.includes('powerpoint') ||
    ['ppt', 'pptx', 'odp'].includes(ext)
  )
    return '📙';
  if (base.includes('calendar') || ext === 'ics') return '📅';
  if (base.startsWith('text/') || ['txt', 'md', 'json', 'xml', 'log'].includes(ext)) return '📄';
  return '📎';
}

/** mm:ss for running recordings. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

export function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function deviceErrorMessage(error: unknown, device: 'Kamera' | 'Mikrofon'): string {
  const name = error instanceof Error ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return `Du hast den Zugriff auf ${device === 'Kamera' ? 'die Kamera' : 'das Mikrofon'} nicht erlaubt. Du kannst ihn in den Browser-Einstellungen freigeben.`;
    case 'NotFoundError':
    case 'OverconstrainedError':
      return `${device} wurde nicht gefunden.`;
    case 'NotReadableError':
    case 'AbortError':
      return `${device} wird gerade von einer anderen App benutzt.`;
    default:
      return `${device} konnte nicht gestartet werden.`;
  }
}

/** Ab hier gibt es den Hinweis, dass die App offen bleiben muss. */
export const GROSS_HINWEIS_AB = 100 * 1024 * 1024;

/** Checks the shared upload ceiling and explains the rejection. */
export function withinUploadLimit(
  kind: AttachmentKind,
  size: number,
  name?: string | null,
): boolean {
  const max = maxUploadBytesFuer(kind);
  if (size <= max) {
    /*
     * Ehrlich vorab: Ein grosser Upload braucht die App im Vordergrund. Der
     * Browser lädt nicht im Hintergrund weiter – auf iOS gar nicht. Besser
     * jetzt ein Satz als später ein Upload, der still stehen blieb, weil das
     * Telefon gesperrt wurde.
     */
    if (size > GROSS_HINWEIS_AB) {
      toast(
        `Grosse Datei (${formatBytes(size)}): Bitte die App offen lassen, bis sie hochgeladen ist – ` +
          'im Hintergrund lädt das Telefon nicht weiter. Abgebrochenes geht später an derselben Stelle weiter.',
        'info',
      );
    }
    return true;
  }
  const subject = name ? `„${name}“` : 'Die Datei';
  toast(`${subject} ist zu groß (${formatBytes(size)}, erlaubt sind ${formatBytes(max)})`, 'error');
  return false;
}

export function supportsRecorder(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined';
}

export function supportsCapture(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
}

/**
 * In welchem Format die Kamera der App aufnimmt – das erste, das der Browser kann.
 *
 * # Warum MP4 mit H.264 und AAC jetzt vorn steht
 *
 * Hier stand WebM mit VP9 an erster Stelle. Das ist auf jedem Telefon
 * abspielbar – aber nicht auf jedem Fernseher: AirPlay nimmt kein WebM, ein
 * Chromecast der ersten drei Generationen kein VP9, und LG-Fernseher in Full HD
 * auch nicht. Wer ein Video aus der App auf den Fernseher schickte, bekam dort
 * „Format nicht unterstützt", während der Code-Weg (der Browser des Fernsehers
 * dekodiert selbst) es zeigte.
 *
 * MP4 mit H.264 High (Level 4.0, `avc1.640028`) und AAC-LC (`mp4a.40.2`)
 * spielt dagegen so gut wie jedes Gerät: Chromecast ab der ersten Generation,
 * AirPlay, Samsung, LG, Fire TV. `MediaRecorder` kann es in Chrome ab 126 (auch
 * auf Android) und in Safari. Die schlichteren Schreibweisen danach fangen
 * Browser auf, die nur bestimmte Kennungen annehmen.
 *
 * Die WebM-Fassungen bleiben als Rückfall stehen – für Firefox und ältere
 * Chrome, die kein MP4 aufnehmen. Ungeprüft ist, ob jeder Empfänger die
 * fragmentierte MP4-Ausgabe von Chrome auch beim Spulen mitmacht; Abspielen
 * von vorn tut er.
 */
export const VIDEO_MIME_CANDIDATES = [
  'video/mp4;codecs=avc1.640028,mp4a.40.2',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4',
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
];

/** iOS Safari only ever records `audio/mp4`. */
export const AUDIO_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/aac',
];

export function pickRecorderMime(candidates: string[]): string | null {
  if (!supportsRecorder() || typeof MediaRecorder.isTypeSupported !== 'function') return null;
  return candidates.find((candidate) => MediaRecorder.isTypeSupported(candidate)) ?? null;
}

/** Drops empty metadata so the server-side upload schema stays happy. */
export function buildAttachment(input: OutboxAttachment): OutboxAttachment {
  const attachment: OutboxAttachment = {
    kind: input.kind,
    mime: baseMime(input.mime) || 'application/octet-stream',
    fileName: input.fileName,
    blob: input.blob,
  };
  if (input.width && input.width > 0) attachment.width = Math.round(input.width);
  if (input.height && input.height > 0) attachment.height = Math.round(input.height);
  if (input.durationMs && input.durationMs > 0)
    attachment.durationMs = Math.round(input.durationMs);
  if (input.waveform && input.waveform.length > 0) attachment.waveform = input.waveform;
  if (input.previewDataUrl && input.previewDataUrl.length <= LIMITS.previewDataUrlMax) {
    attachment.previewDataUrl = input.previewDataUrl;
  }
  return attachment;
}

/**
 * Hands the media over to the chat store, which uploads it (also offline, via
 * the outbox). Modules never talk to `api.media` directly.
 */
export async function sendMedia(
  conversationId: string,
  type: MessageType,
  caption: string | null,
  attachments: OutboxAttachment[],
): Promise<boolean> {
  try {
    await useChat.getState().sendMessage(conversationId, {
      type,
      body: caption && caption.trim().length > 0 ? caption.trim() : null,
      attachments,
    });
    return true;
  } catch (error) {
    toast(errorMessage(error, 'Senden fehlgeschlagen'), 'error');
    return false;
  }
}

/** Object URL for a blob that is revoked as soon as the blob changes. */
export function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return undefined;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [blob]);
  return url;
}

/** Only one voice message / video plays at a time. */
let activePlayback: HTMLMediaElement | null = null;

export function claimPlayback(element: HTMLMediaElement): void {
  if (activePlayback && activePlayback !== element) activePlayback.pause();
  activePlayback = element;
}

export function releasePlayback(element: HTMLMediaElement): void {
  if (activePlayback === element) activePlayback = null;
}

/** Stable pseudo waveform for older voice messages without stored peaks. */
export function fallbackPeaks(seed: string, count = 48): number[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  const peaks: number[] = [];
  for (let i = 0; i < count; i += 1) {
    hash = (hash * 1664525 + 1013904223) >>> 0;
    peaks.push(0.25 + ((hash >>> 8) % 1000) / 1400);
  }
  return peaks;
}

/**
 * Wohin gesprungen wird, um einen echten Einzelbildrahmen zu erzwingen.
 *
 * Ein Zehntel und nicht Null: Auf Null steht die Uhr schon, und ein Sprung auf
 * die Stelle, an der man ohnehin steht, ist kein Sprung – der Browser tut
 * dann gar nichts. Ein Zehntel liegt bei jeder üblichen Bildrate hinter dem
 * ersten Bild, aber so nah davor, dass der Unterschied nicht zu sehen ist.
 */
export const STANDBILD_ZEIT = 0.1;

/**
 * Auf welche Stelle gesprungen wird, bei gegebener Länge.
 *
 * Eigene Funktion, weil hier zwei Sonderfälle zusammenkommen, die man im
 * Browser nur mühsam herbeiführt:
 *
 *   * `Infinity`. Eine Aufnahme aus dem eigenen Rekorder hat fast immer keine
 *     Länge im Kopf stehen – der Bildschreiber kennt sie beim Schreiben noch
 *     nicht. Das ist KEIN Grund, den Sprung zu lassen: Die Daten sind da, nur
 *     die Zahl fehlt. Ein früher Entwurf verlangte hier eine endliche Länge,
 *     und damit hätte ausgerechnet das selbst aufgenommene Video kein
 *     Standbild bekommen.
 *   * Ein Video, das kürzer ist als der Sprung. Bei drei Hundertstel läge ein
 *     Zehntel hinter dem Ende, und der Sprung ginge ins Leere. Die Hälfte der
 *     Länge liegt immer drin.
 */
export function standbildZiel(laenge: number): number {
  if (Number.isFinite(laenge) && laenge > 0) return Math.min(STANDBILD_ZEIT, laenge / 2);
  return STANDBILD_ZEIT;
}

/**
 * Das erste Bild eines Videos wirklich zeigen, statt nur den Klecks davor.
 *
 * # Warum die Vorschau dauerhaft unscharf war
 *
 * An jedem `<video>` stand `poster={previewDataUrl}` zusammen mit
 * `preload="metadata"`. Beides zusammen heisst: Der Browser holt die Kopfdaten
 * – Länge, Masse, Tonspur – und KEINEN einzigen Bildpunkt. Zu sehen ist
 * deshalb ausschliesslich das Plakat, und das Plakat ist die eingebettete
 * Vorschau: früher 48 Punkte Kante, heute 160, aber in jedem Fall ein
 * hochskalierter Klecks. Das war nicht „die Vorschau lädt noch“ – das WAR das
 * Video, solange niemand auf Abspielen drückte.
 *
 * `preload="auto"` wäre die naheliegende Antwort und die falsche: Damit lädt
 * jedes Video in jeder sichtbaren Blase von selbst, und ein Chatverlauf mit
 * zehn Videos zöge hunderte Megabyte über die Mobilverbindung.
 *
 * # Was statt dessen geschieht
 *
 * Es wird GESPRUNGEN. Ein Sprung zwingt den Browser, genau die Stelle zu
 * holen und zu entschlüsseln, auf die gesprungen wird – ein paar Kilobyte
 * statt der ganzen Datei. Danach steht ein echtes, scharfes Bild im Rahmen.
 *
 * Und anschliessend zurück auf Null: Sonst begänne das Abspielen bei einem
 * Zehntel, und der Anfang fehlte. Der zweite Sprung kostet nichts, weil die
 * Daten um Null herum nach dem ersten bereits im Zwischenspeicher liegen, und
 * er ist nicht zu sehen, weil beide Bilder ein Zehntel auseinanderliegen.
 *
 * # Die Merker am Element
 *
 * `dataset.standbild` hält fest, wie weit das Ganze ist. Ohne den Merker
 * liefe es im Kreis: Der zweite Sprung meldet wieder `seeked`, und das Video
 * stünde wieder auf Null – also wieder der Zustand, in dem gesprungen wird.
 * Der Merker ist ausserdem das, was die Prüfung liest.
 */
export function standbildHolen(video: HTMLVideoElement | null | undefined): void {
  if (!video) return;
  // Schon erledigt oder schon unterwegs.
  if (video.dataset.standbild) return;
  // Wer schon abspielt oder von Hand weitergespult hat, wird nicht zurückgerissen.
  if (!video.paused || video.currentTime > 0) return;

  const ziel = standbildZiel(video.duration);
  if (!(ziel > 0)) return;

  video.dataset.standbild = 'gesucht';
  const zurueck = () => {
    video.removeEventListener('seeked', zurueck);
    video.dataset.standbild = 'fertig';
    if (video.paused && video.currentTime > 0) {
      try {
        video.currentTime = 0;
      } catch {
        /* Ein Video, das nicht zurückspringen will, spielt eben ab einem Zehntel. */
      }
    }
  };
  video.addEventListener('seeked', zurueck);
  try {
    video.currentTime = ziel;
  } catch {
    // Manche Quellen lassen sich nicht spulen; dann bleibt die unscharfe Vorschau.
    video.removeEventListener('seeked', zurueck);
    delete video.dataset.standbild;
  }
}
